import { cn, convertNumber } from '@/helpers/utils/functionality-utils';
import { useTranslation } from '@/hooks/useTranslation';
import { NotificationsAsPopover } from '@/modules/main/dashboard/staff-dashboard/components/notifications/NotificationAsPopover';
import { TasksAsPopover } from '@/modules/main/dashboard/staff-dashboard/components/tasks/TasksAsPopover';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useEffect, useState, type RefObject } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useGetMyTasksCompletedMutation,
  useGetMyTasksPendingMutation,
  useGetTasksAssignedByMeCompletedMutation,
  useGetTasksAssignedByMePendingMutation,
} from '@/store/apis/user/tasksApiCalls';
import { useGetMyNotificationsMutation } from '@/store/apis/user/notificationApiCalls';
import { staffDActions } from '@/store/slices/main/dashboard/staffDashboardMyTaskSlice';
import { TbBell, TbClipboardCheck } from 'react-icons/tb';
import { useGetMyAlarmsMutation } from '@/store/apis/user/alarmsApiCalls';
import { AlarmsAsPopover } from '@/modules/main/dashboard/staff-dashboard/components/alarms/AlarmsAsPopover';
import { GrAlarm } from 'react-icons/gr';
import { Button } from 'primereact/button';

export const UserNotifPanel = ({
  isMobileMenu,
  nr,
  tr,
  ar,
}: {
  isMobileMenu?: boolean;
  nr: RefObject<OverlayPanel | null>;
  tr: RefObject<OverlayPanel | null>;
  ar: RefObject<OverlayPanel | null>;
}) => {
  const dispatch = useAppDispatch();
  const { t: ttasks } = useTranslation('tasks');
  const { t: tcommon } = useTranslation('common');

  const userPermissions = useAppSelector(
    (state) => state.user.authUserData.userPermissions,
  );

  const [getMyTasksPending, { isLoading: isLoadingPendingTasks }] =
    useGetMyTasksPendingMutation({
      fixedCacheKey: 'get-my-tasks-pending',
    });
  const [getMyTasksCompleted] = useGetMyTasksPendingMutation({
    fixedCacheKey: 'get-my-tasks-completed',
  });
  const [getAssignedTasksPending] = useGetTasksAssignedByMePendingMutation({
    fixedCacheKey: 'get-assigned-tasks-pending',
  });
  const [getAssignedTasksCompleted] = useGetTasksAssignedByMeCompletedMutation({
    fixedCacheKey: 'get-assigned-tasks-completed',
  });
  const [getMyNotifications, { isLoading: isLoadingNotifs }] =
    useGetMyNotificationsMutation({
      fixedCacheKey: 'get-my-notifs',
    });

  const [getMyAlarms, { isLoading: isLoadingAlarms }] = useGetMyAlarmsMutation({
    fixedCacheKey: 'get-my-alarms',
  });

  const [getMyNotifsForUpdate] = useGetMyNotificationsMutation();
  const [getMyAlarmForUpdate] = useGetMyAlarmsMutation();
  const [getTasksPendingForUpdate] = useGetMyTasksPendingMutation();
  const [getTasksCompletedForUpdate] = useGetMyTasksCompletedMutation();

  const isNewTaskAdded = useAppSelector(
    (state) => state.common.tasks.newTaskAdded,
  );
  const isNewAlarmIncoming = useAppSelector(
    (state) => state.common.tasks.newAlarmIncoming,
  );
  const isNewNotificationIncoming = useAppSelector(
    (state) => state.common.tasks.newNotificationIncoming,
  );

  const [showDotNotif, setShowDotNotif] = useState(false);
  const [showDotTask, setShowDotTask] = useState(false);
  const [showDotAlarm, setShowDotAlarm] = useState(false);

  const authUser = useAppSelector((state) => state?.user?.authUserData?.data);
  const showNotif =
    userPermissions['notifications_view'] &&
    convertNumber(authUser?.tenant?.settings?.allowed_notifications?.length) >
      0;
  const showAlarm =
    userPermissions['alarms_view'] &&
    convertNumber(authUser?.tenant?.settings?.allowed_alarms?.length) > 0;

  const updateNotifs = () => {
    getMyNotifsForUpdate({ pg: null }).then((res) => {
      dispatch(staffDActions.updateNotifications(res?.data?.data || []));
    });
  };

  const updateAlarms = () => {
    getMyAlarmForUpdate({ pg: null }).then((res) => {
      dispatch(staffDActions.updateAlarms(res?.data?.data || []));
    });
  };

  const updateTasks = () => {
    getTasksPendingForUpdate({ pg: null }).then((res) => {
      dispatch(staffDActions.updatePendingTasks(res?.data?.data || []));
    });
    getTasksCompletedForUpdate({ pg: null }).then((res) => {
      dispatch(staffDActions.updateCompletedTasks(res?.data?.data || []));
    });
  };

  useEffect(() => {
    if (isNewTaskAdded) {
      setShowDotTask(true);
      updateTasks();
    }
    if (isNewAlarmIncoming) {
      setShowDotAlarm(true);
      updateAlarms();
    }
    if (isNewNotificationIncoming) {
      setShowDotNotif(true);
      updateNotifs();
    }
  }, [isNewTaskAdded, isNewAlarmIncoming, isNewNotificationIncoming]);

  useEffect(() => {
    async function pt() {
      try {
        const [
          pendingRes,
          completedRes,
          assignedByMePendingRes,
          assignedByMeCompletedRes,
          notificationsRes,
          alarmsRes,
        ] = await Promise.allSettled([
          getMyTasksPending({ pg: null }),
          getMyTasksCompleted({ pg: null }),
          getAssignedTasksPending({ pg: null }),
          getAssignedTasksCompleted({ pg: null }),
          getMyNotifications({ pg: null }),
          getMyAlarms({ pg: null }),
        ]);

        if (pendingRes.status === 'fulfilled') {
          dispatch(
            staffDActions.setPendingTasks(pendingRes.value?.data?.data || []),
          );
        }

        if (completedRes.status === 'fulfilled') {
          dispatch(
            staffDActions.setCompletedTasks(
              completedRes.value?.data?.data || [],
            ),
          );
        }

        if (assignedByMePendingRes.status === 'fulfilled') {
          dispatch(
            staffDActions.setPendingTasksABM(
              assignedByMePendingRes.value?.data?.data || [],
            ),
          );
        }

        if (assignedByMeCompletedRes.status === 'fulfilled') {
          dispatch(
            staffDActions.setCompletedTasksABM(
              assignedByMeCompletedRes.value?.data?.data || [],
            ),
          );
        }

        if (notificationsRes.status === 'fulfilled') {
          dispatch(
            staffDActions.setMyNotifications(
              notificationsRes.value?.data?.data || [],
            ),
          );
        }

        if (alarmsRes.status === 'fulfilled') {
          dispatch(
            staffDActions.setMyAlarms(alarmsRes.value?.data?.data || []),
          );
        }
      } catch (error) {
        console.error(error);
      }
    }

    pt();
  }, []);

  const tasksButton = userPermissions['tasks_view'] ? (
    isMobileMenu ? (
      <Button
        onClick={(e) => {
          tr.current?.toggle(e);
          setShowDotTask(false);
        }}
        text
        severity="secondary"
        className="flex items-center gap-3"
      >
        <div className="flex items-center gap-3">
          <div className="relative size-5 shrink-0">
            <TbClipboardCheck className="absolute right-1/2 top-1/2 size-6 -translate-y-1/2 translate-x-1/2" />
            {showDotTask && (
              <i className="pi pi-circle-fill absolute right-2 top-2 !text-xs text-red-500"></i>
            )}
          </div>
          {ttasks('tasks')}
        </div>
      </Button>
    ) : (
      <button
        title={ttasks('tasks')}
        type="button"
        className="p-link layout-topbar-button"
        onClick={(e) => {
          tr.current?.toggle(e);
          setShowDotTask(false);
        }}
      >
        {showDotTask && (
          <i className="pi pi-circle-fill absolute right-2 top-2 !text-xs text-red-500"></i>
        )}
        <TbClipboardCheck className="mr-2 text-3xl sm:mr-0" />
        <span className="ml-2">{ttasks('tasks')}</span>
      </button>
    )
  ) : undefined;
  const alarmsButton = showAlarm ? (
    isMobileMenu ? (
      <Button
        onClick={(e) => {
          setShowDotAlarm(false);
          ar.current?.toggle(e);
        }}
        text
        severity="secondary"
        className="flex items-center gap-3"
      >
        <div className="relative size-5 shrink-0">
          <GrAlarm className="absolute right-1/2 top-1/2 size-full -translate-y-1/2 translate-x-1/2" />
          {showDotAlarm && (
            <i className="pi pi-circle-fill absolute right-2 top-2 !text-xs text-red-500"></i>
          )}
        </div>
        {ttasks('alarms')}
      </Button>
    ) : (
      <button
        title={ttasks('alarms')}
        type="button"
        className="p-link layout-topbar-button"
        onClick={(e) => {
          setShowDotAlarm(false);
          ar.current?.toggle(e);
        }}
      >
        {showDotAlarm && (
          <i className="pi pi-circle-fill absolute right-2 top-2 !text-xs text-red-500"></i>
        )}
        <GrAlarm className="mr-2 text-[1.6rem] sm:mr-1" />
        <span className="ml-2">{ttasks('alarms')}</span>
      </button>
    )
  ) : undefined;
  const notifButton = showNotif ? (
    isMobileMenu ? (
      <Button
        onClick={(e) => {
          setShowDotNotif(false);
          nr.current?.toggle(e);
        }}
        text
        severity="secondary"
        className="flex items-center gap-3"
      >
        <div className="relative size-5 shrink-0">
          <TbBell className="absolute right-1/2 top-1/2 size-6 -translate-y-1/2 translate-x-1/2" />
          {showDotNotif && (
            <i className="pi pi-circle-fill absolute right-2 top-2 !text-xs text-red-500"></i>
          )}
        </div>
        {tcommon('Notification')}
      </Button>
    ) : (
      <button
        title={tcommon('Notification')}
        type="button"
        className="p-link layout-topbar-button"
        onClick={(e) => {
          setShowDotNotif(false);
          nr.current?.toggle(e);
        }}
      >
        {showDotNotif && (
          <i className="pi pi-circle-fill absolute right-2 top-2 !text-xs text-red-500"></i>
        )}
        <TbBell className="mr-2 text-3xl sm:mr-0" />
        <span className="ml-2">{tcommon('Notification')}</span>
      </button>
    )
  ) : undefined;

  return (
    <>
      <OverlayPanel
        // appendTo={'self'}
        className={cn('menu-ref flex h-[470px] w-[450px]', {
          hidden: !showNotif,
        })}
        pt={{ content: { className: 'size-full' } }}
        ref={nr}
      >
        <NotificationsAsPopover isFetching={isLoadingNotifs} overlayRef={nr} />
      </OverlayPanel>
      {showAlarm && (
        <OverlayPanel
          pt={{ content: { className: 'size-full' } }}
          className="menu-ref flex h-[470px] w-[450px]"
          ref={ar}
        >
          <AlarmsAsPopover overlayRef={ar} isFetching={isLoadingAlarms} />
        </OverlayPanel>
      )}
      <OverlayPanel
        className="menu-ref flex h-[470px] w-[450px]"
        pt={{ content: { className: 'size-full' } }}
        ref={tr}
      >
        <TasksAsPopover isFetching={isLoadingPendingTasks} overlayRef={tr} />
      </OverlayPanel>
      {tasksButton}
      {alarmsButton}
      {notifButton}
    </>
  );
};
